/* eslint-disable local-rules/no-exported-domain-types-outside-domain */
"use client";

/**
 * `useTweaks(contextName, spec, reveals?)` — the v2 registration hook.
 *
 * The call CREATES the mark/option items in the context JSON on first render
 * (registration is the source of truth) and returns one boolean per option:
 *
 * ```tsx
 * const { option1, option2Variant1, option2Variant2, option3, markRef } =
 *   useTweaks("context-name-1", {
 *     markName1: ["option1", ["option2Variant1", "option2Variant2"], "option3"],
 *   });
 * ```
 *
 * A nested array is a variant sub-group — and sub-groups nest to arbitrary
 * depth. Result keys are the EXACT option strings from the spec (name options
 * in camelCase to keep them destructurable). Every mark has exactly ONE
 * current option at all times: the stored `selectedOptionName`, which
 * defaults to the reserved `existing` option. The boolean of the selected
 * option is true; when `existing` is selected (or the provider is absent, or
 * this is production) ALL booleans are false and the component's fall-through
 * branch — the existing rendering — runs.
 *
 * The optional third argument carries per-mark reveal metadata (census F08):
 * where the mark's options can actually be SEEN and how to put the page into
 * that state. It rides registration/provider/beacon only — never the store.
 *
 * Reserved result keys, never valid option names:
 *  - `markRef(markName)` — attach to the mark's element so the /dev/tweaks
 *    page can locate it (scroll + flash) through the beacon;
 *  - `controlValue({mark, option, control})` — current value of a
 *    handler-authored per-option control (the `existing` option included), or
 *    null when absent/disabled (callers keep their code default behind
 *    `?? defaultValue`).
 */

import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import type { RefCallback } from "react";
import { useTweaksProviderValue } from "./provider";
import {
  normalizeTweaksSpec,
  registrationLeafNames,
  storedMarkLeafOptions,
} from "./types";
import type {
  TweakControlValue,
  TweakMark,
  TweaksRevealSpec,
  TweaksSpec,
} from "./types";

export type TweakMarkRefRegistrar = (
  markName: string,
) => RefCallback<HTMLElement>;

export type TweakControlValueReader = (args: {
  mark: string;
  option: string;
  control: string;
}) => TweakControlValue | null;

export type UseTweaksResult = Readonly<Record<string, boolean>> & {
  markRef: TweakMarkRefRegistrar;
  controlValue: TweakControlValueReader;
};

export function useTweaks(
  contextName: string,
  spec: TweaksSpec,
  reveals?: TweaksRevealSpec,
): UseTweaksResult {
  const provider = useTweaksProviderValue();
  const instanceId = useId();

  // Specs are inline object literals, so identity changes every render; the
  // serialized form keys a stable normalization.
  const specKey = JSON.stringify(spec);
  const normalized = useMemo(
    () => normalizeTweaksSpec(JSON.parse(specKey) as TweaksSpec),
    [specKey],
  );

  useEffect(() => {
    if (provider && normalized.problems.length > 0) {
      console.error(
        `[tweaks2] Invalid registration for context "${contextName}" (offending marks were skipped):\n${normalized.problems.join("\n")}`,
      );
    }
  }, [provider, normalized, contextName]);

  const registerMarks = provider?.register;
  const unregisterMarks = provider?.unregister;
  useEffect(() => {
    if (!registerMarks || !unregisterMarks || normalized.marks.length === 0) {
      return;
    }
    registerMarks({
      instanceId,
      context: contextName,
      marks: normalized.marks,
    });
    return () => unregisterMarks({ instanceId });
  }, [registerMarks, unregisterMarks, instanceId, contextName, normalized]);

  // Reveal metadata carries live callbacks, so its identity changes every
  // render; hand the latest to the provider each render — the provider
  // rebroadcasts presence only when the DESCRIPTOR content changes.
  const setMarkReveals = provider?.setMarkReveals;
  useEffect(() => {
    if (!setMarkReveals) {
      return;
    }
    setMarkReveals({ instanceId, context: contextName, reveals: reveals ?? {} });
  });

  const contextFile = provider?.files[contextName];
  const storedMarksByName = useMemo(
    () =>
      new Map<string, TweakMark>(
        (contextFile?.marks ?? []).map((mark) => [mark.name, mark]),
      ),
    [contextFile],
  );

  const booleans = useMemo(() => {
    const record: Record<string, boolean> = {};
    normalized.marks.forEach((registrationMark) => {
      const storedMark = storedMarksByName.get(registrationMark.name);
      // The ONE current selection drives rendering. The reserved `existing`
      // name matches no registered option, so it (and an absent store row)
      // lands on all-false — the fall-through branch.
      const selectedName = storedMark ? storedMark.selectedOptionName : null;
      registrationLeafNames(registrationMark).forEach((leafName) => {
        record[leafName] = leafName === selectedName;
      });
    });
    return record;
  }, [normalized, storedMarksByName]);

  // Ref callbacks must keep a stable identity across renders (otherwise React
  // detaches/reattaches every render), while always reporting into the CURRENT
  // provider/context — hence a cached callback per mark reading latest args
  // through a ref.
  const setMarkElement = provider?.setMarkElement;
  const registrarArgsRef = useRef({ setMarkElement, instanceId, contextName });
  useEffect(() => {
    registrarArgsRef.current = { setMarkElement, instanceId, contextName };
  }, [setMarkElement, instanceId, contextName]);
  const markRefCallbacksRef = useRef(
    new Map<string, RefCallback<HTMLElement>>(),
  );
  const markRef = useCallback<TweakMarkRefRegistrar>((markName) => {
    const cachedCallback = markRefCallbacksRef.current.get(markName);
    if (cachedCallback) {
      return cachedCallback;
    }
    const refCallback: RefCallback<HTMLElement> = (element) => {
      const currentArgs = registrarArgsRef.current;
      currentArgs.setMarkElement?.({
        instanceId: currentArgs.instanceId,
        context: currentArgs.contextName,
        mark: markName,
        element,
      });
    };
    markRefCallbacksRef.current.set(markName, refCallback);
    return refCallback;
  }, []);

  const controlValue = useCallback<TweakControlValueReader>(
    ({ mark, option, control }) => {
      const storedMark = storedMarksByName.get(mark);
      if (!storedMark) {
        return null;
      }
      const storedOption = storedMarkLeafOptions(storedMark).find(
        (leafOption) => leafOption.name === option,
      );
      const storedControl = storedOption?.controls.find(
        (candidate) => candidate.id === control,
      );
      return storedControl ? storedControl.value : null;
    },
    [storedMarksByName],
  );

  return useMemo(
    () => ({ ...booleans, markRef, controlValue }) as UseTweaksResult,
    [booleans, markRef, controlValue],
  );
}
